import React from 'react';

import NotificationMessage from '~/components/ClaimPage/CliveApp/CliveNotifications/NotificationMessage';
import type { StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';
import Text from '~/components/core/TextComponents/Text';
import cn from '~/Utils/cn';

import CardMeta, { CardTime } from '../../CardMeta';
import { KIND_APPEARANCE, LOG_MESSAGE_VISIBLE_LINES } from '../../constants';
import type { ActionStateAppearance, ActivityKind } from '../../types';
import { formatEntryDateTime } from '../../utils';

interface KindCardFrameProps {
  notification: StpNotification;
  /** The colours of the state the thing is in. */
  appearance: ActionStateAppearance;
  /** What sort of thing it is, which decides the icon and its colours, whatever the state. */
  kind: ActivityKind;
  title?: string | null;
  subtitle?: string;
  /**
   * What became of the thing, or which message a question raised in reply answers, on a line of its own over
   * the title.
   */
  chip?: React.ReactNode;
  /** How sure Clive was of the thing, beside the chip, where it sent a score. */
  confidence?: React.ReactNode;
  /** Outlines the card: the activity panel last took the reader here. */
  isSelected?: boolean;
  /** Shows the thing's card in the activity panel, where it has one, from the title. */
  onSelect?: () => void;
  /** The DOM id of the activity panel card `onSelect` shows, named on the title as what it controls. */
  controls?: string;
  /**
   * Drawn inside another card, as a question folded into Clive's reply: the card it sits in has the rail's node
   * and the time, so this one draws neither.
   */
  isFolded?: boolean;
  children?: React.ReactNode;
}

/**
 * One card layout for every kind of thing Clive raises in the log. Only the icon, the colours, the
 * line over the title and what follows the body change from one kind to the next, so a reader who has
 * learnt to read one card can read them all.
 */
const KindCardFrame: React.FC<KindCardFrameProps> = ({
  notification,
  appearance,
  kind,
  title,
  subtitle,
  chip,
  confidence,
  isSelected,
  onSelect,
  controls,
  isFolded,
  children,
}) => {
  const { message, timestamp } = notification;
  const { Icon, chip: iconChip, iconColor } = KIND_APPEARANCE[kind];
  const time = formatEntryDateTime(timestamp);
  const titleText = title ? (
    <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="wrap-anywhere">
      {title}
    </Text>
  ) : null;

  return (
    <div className="flex gap-12">
      {isFolded ? null : (
        <div className="flex w-20 shrink-0 justify-center pt-[5px]">
          <div className={cn('h-[12px] w-[12px] rounded-full', appearance.node)} aria-hidden />
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-4">
        {isFolded ? null : <CardTime time={time} />}

        <div
          className={cn(
            'flex min-w-0 flex-col gap-8 rounded-lg border border-solid bg-white px-16 py-12',
            appearance.card,
            { 'outline outline-2 outline-offset-4 outline-blue-600': isSelected }
          )}
        >
          <CardMeta status={chip} confidence={confidence} />

          <div className="flex items-start gap-12">
            <div className={cn('grid h-32 w-32 shrink-0 place-items-center rounded-lg', iconChip)}>
              {/* The kind's icon and colours, from the one place that decides them: a thing must not
                wear one mark here and another in the activity panel. */}
              <Icon size={18} iconColor={iconColor} />
            </div>

            <div className="min-w-0 flex-1">
              {/* The title line is the icon's height, so the icon and a one-line title share a midline.
                The title has the column to itself: the chip and the confidence sit on a line of their own
                above, where they cannot narrow the title to a few words a line. */}
              {titleText ? (
                <div className="flex min-h-32 items-center">
                  {onSelect ? (
                    // The title alone, not the whole card: the card holds its own controls — Execute,
                    // Reject, an answer — and each of those acts on itself and nothing else.
                    <button
                      type="button"
                      onClick={onSelect}
                      aria-controls={controls}
                      className="cursor-pointer border-0 bg-transparent p-0 text-left"
                    >
                      {titleText}
                    </button>
                  ) : (
                    titleText
                  )}
                </div>
              ) : null}
              {subtitle ? (
                // Never cut short: it wraps to the column, as the title does.
                <Text
                  variant={Text.VARIANTS.XS}
                  weight={Text.WEIGHTS.REGULAR}
                  colorVariant={Text.COLOR_VARIANTS.SECONDARY}
                  className="wrap-anywhere"
                >
                  {subtitle}
                </Text>
              ) : null}
            </div>
          </div>

          {message ? <NotificationMessage message={message} visibleLines={LOG_MESSAGE_VISIBLE_LINES} /> : null}

          {children}
        </div>
      </div>
    </div>
  );
};

export default KindCardFrame;
